import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { FaChevronDown, FaChevronUp, FaFilter, FaSearch, FaSort, FaTimes } from 'react-icons/fa';
import { ShoppingCart } from "lucide-react";
import FavoriteButton from "./FavoriteButton";
import { useLanguage } from "./LanguageContext";
import { useDataRefresh } from "./liveUpdates/DataRefreshProvider";
import axios from "axios";
import { api } from "../lib/http";


const API_BASE_URL = "https://shopaholics.ge/api";

/* ================= THEME ================= */
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  card: "#fff",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

const BP = {
  md: "1200px",
  sm: "900px",
  xs: "600px",
};

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 12px clamp(10px, 4vw, 16px) 28px; /* მობილურზე უფრო პატარა გვერდითა padding */
  overflow-x: hidden;
`;

const GridWrap = styled(Container)`
  padding-top: 18px;
  overflow-x: hidden;

  /* მობილურზე – გვერდითი padding = 0 */
  padding-left: 0;
  padding-right: 0;

  /* ტაბლეტიდან ზემოთ დავაბრუნოთ ნორმალური შინაგანი გვერდითი padding */
  @media (min-width: 641px) {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

/* ================= LOADER ================= */
const spin = keyframes`to { transform: rotate(360deg); }`;
const PageLoader = styled.div`
  display: grid; place-items: center; min-height: 60vh;
`;
const Spinner = styled.div`
  width: 42px; height: 42px; border-radius: 50%;
  border: 4px solid rgba(0,0,0,.1); border-top-color: ${COLORS.text};
  animation: ${spin} 1s linear infinite;
`;

/* ================= TOP BAR (Filters/Sort + Applied chips) ================= */
const TopBar = styled.div`
  position: sticky;
  top: 86px; /* თუ ჰედერი სხვა სიმაღლისაა, მოარგე */
  z-index: 5;
  background: rgba(255,255,255,.8);
  backdrop-filter: saturate(180%) blur(8px);
  border-bottom: 1px solid ${COLORS.line};
  @media (max-width: 900px) {
    top: 150px; /* თუ ჰედერი სხვა სიმაღლისაა, მოარგე */
  }
`;

const TopBarInner = styled(Container)`
  display: flex; align-items: center; gap: 10px; justify-content: space-between;
  padding-top: 10px; padding-bottom: 10px;
  overflow: visible;
`;

const LeftActions = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap;
`;

const RightActions = styled.div`
  position: relative;
`;

const PillButton = styled.button`
  appearance: none; border: 1px solid ${COLORS.line};
  background: #fff; color: ${COLORS.text};
  border-radius: 999px; height: 40px; padding: 0 14px;
  display: inline-flex; align-items: center; gap: 8px;
  font-weight: 700; cursor: pointer;
  transition: box-shadow .15s ease, transform .15s ease, border-color .15s ease;
  &:hover { box-shadow: ${COLORS.shadow}; transform: translateY(-1px); }
`;

const SortDropdownWrap = styled.div`
  position: relative;
`;

const DropdownCard = styled.div`
  position: absolute; top: calc(100% + 8px); right: 0;
  min-width: 220px; background: #fff; border: 1px solid ${COLORS.line};
  border-radius: 14px; box-shadow: ${COLORS.shadow}; padding: 8px;
  z-index: 20;
  ${({ $hidden }) => $hidden && css`display:none;`}
`;
const DropdownItem = styled.button`
  width: 100%; text-align: left; border: 0; background: transparent;
  padding: 10px 12px; border-radius: 10px; font-weight: 600; cursor: pointer;
  &:hover { background: ${COLORS.subtle}; }
`;

/* ================= APPLIED FILTERS ================= */
const AppliedFiltersBox = styled(Container)`
  padding-top: 10px; display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
`;
const FilterTag = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  background: #f0f0f1; color: ${COLORS.text};
  border: 1px solid ${COLORS.line}; border-radius: 999px;
  padding: 6px 10px; font-size: 13px;
  .close { display: inline-flex; align-items: center; cursor: pointer; opacity: .7; }
  .close:hover { opacity: 1; }
`;
/* Overlay იგივე დარჩეს, უბრალოდ z-index და pointer-events ნათლად: */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.35);
  z-index: 3005;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  transition: .2s;
`;

/* Drawer: დააყენე 100dvh და grid-rows, რომ ზედა/ქვედა ნაწილის ქვეშ შუა ბლოკი დაესქროლოს */
const FiltersMenu = styled.aside`
  position: fixed;
  top: 0;
  left: 0;
  height: 100dvh;               /* <- სტაბილური viewport height */
  width: 86vw;
  max-width: 360px;
  background: #ffffff;
  border-right: 1px solid rgba(0,0,0,.08);
  transform: translateX(${({ isVisible }) => (isVisible ? "0" : "-100%")});
  transition: transform .25s ease;
  z-index: 3006;
  display: grid;
  grid-template-rows: auto 1fr auto;  /* header | scrollable body | footer */
  overflow: hidden;                    /* overflow გარეთ არა */
    overflow-y: auto;                 /* <- სკროლი მთელი სექციისთვის */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

`;

/* Header/Close ზუსტად გაქვს, შევინარჩუნე */
const FilterTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0,0,0,.08);
  h3 { margin: 0; font-size: 16px; }
`;

const CloseButton = styled.button`
  background: #fff;
  border: 1px solid rgba(0,0,0,.08);
  border-radius: 10px;
  height: 36px; width: 36px;
  display: grid; place-items: center;
  cursor: pointer;
`;

/* აი აქაა მთავარი ფიქსი: min-height:0 + overflow-y:auto + iOS inertia */
const FilterBody = styled.div`
  padding: 12px 14px 18px;
  display: grid;
  gap: 14px;
  min-height: 0;                    /* <- აუცილებელია Grid-ში რომ იმუშაოს სკროლმა */
  overflow-y: auto;                 /* <- სკროლი მთელი სექციისთვის */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
    overflow-x: hidden;
`;

/* თითო სექცია იგივეა, მაგრამ შიგნით სია იყოს დამოუკიდებლად სკროლირებადი როცა გახსნილია */
const FilterSection = styled.div`
  border: 1px solid rgba(0,0,0,.08);
  border-radius: 12px;
  background: #fff;
`;

  const SearchInput = styled.input`
    border: none;
    outline: none;
    flex: 1;
    font-size: 1rem;
  `;


const FilterHeaderBtn = styled.button`
  width: 100%;
  border: 0;
  background: #fff;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 800;
  cursor: pointer;
  user-select: none;
`;

/* აქ დავტოვე კოლაფსის ანიმაცია, მაგრამ შიგნით ჩავსვი scroll wrapper */
const FilterOptions = styled.div`
  /* მთავარი: ვაქცევთ flex column-ს, რომ .scroll დაიკავოს დარჩენილი სიმაღლე */
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* უფრო მაღალი შიდა სივრცე ბრენდებისა ($tall) და არომატებისთვის */
  max-height: ${({ isExpanded, $tall }) =>
    isExpanded ? ($tall ? '68vh' : '52vh') : '0'};

  /* დახურვისას ჩატრიალებულმა ბლოკმა არ დაიჭიროს მოვლენები */
  pointer-events: ${({ isExpanded }) => (isExpanded ? 'auto' : 'none')};
  transition: max-height .25s ease;

  /* ზედა ზონა (ძიება) – წებოვანი, რომ სქროლვისას არ დაიკარგოს */
  > .content {
    position: sticky;
    top: 0;
    padding: 12px;
    background: #fff;
    z-index: 1;
    border-bottom: 1px solid rgba(0,0,0,.06);
  }

  /* სია – რეალური სქროლი */
  > .scroll {
    flex: 1;
    min-height: 0;                  /* <- აუცილებელი nested scroll-სთვის */
    overflow-y: auto;
    padding: 8px 12px 12px;

    /* უკეთესი scroll ქცევა მობილურზე */
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: pan-y;

    /* scrollbar cosmetics */
    scrollbar-width: thin;
  }
  > .scroll::-webkit-scrollbar { width: 8px; }
  > .scroll::-webkit-scrollbar-thumb {
    background: #a15a5a;
    border-radius: 8px;
  }

  @media (max-width: 768px) {
    max-height: ${({ isExpanded }) => (isExpanded ? '60vh' : '0')};
  }
`;

/* ================= SIDE FILTERS (Drawer) ================= */
const SearchContainer = styled.div`
  display: flex; align-items: center; gap: 8px;
  border: 1px solid ${COLORS.line}; border-radius: 10px; padding: 8px 10px; background: #fff;
  input{ border:0; outline:0; flex:1; font-size:14px; }
  svg{ color:${COLORS.dim}; }
`;
const CheckboxList = styled.div` display: grid; gap: 10px; `;
const CheckboxItem = styled.label`
  display: grid; grid-template-columns: 22px 1fr; align-items:center; gap: 10px; cursor: pointer; font-size: 14px;
  input{ display:none; }
  input + span{ width: 20px; height: 20px; border:1px solid #cfcfcf; border-radius: 4px; background: #fff; display:inline-block; position:relative; }
  input:checked + span{ background: ${COLORS.brand}; border-color: ${COLORS.brand}; }
  input:checked + span::after{ content:''; position:absolute; inset: 4px; border: 2px solid #fff; border-top: 0; border-left: 0; transform: rotate(45deg); }
`;
const PriceFilterContainer = styled.div`
  background:#fafafa; border:1px solid ${COLORS.line}; border-radius: 10px; padding: 12px; display:grid; gap:12px;
`;
const PriceRangeWrapper = styled.div` display:grid; gap:6px; `;
const PriceLabel = styled.label` font-weight:800; font-size: 14px; display:flex; justify-content:space-between; color:${COLORS.text}; `;
const PriceRangeInput = styled.input`
  width: 100%; height: 6px; border-radius: 999px; outline: none; -webkit-appearance: none; background: linear-gradient(to right, ${COLORS.brand} 0%, #e7c2c2 100%);
  &::-webkit-slider-thumb{ appearance:none; width:18px; height:18px; border-radius: 50%; background: ${COLORS.brand}; cursor:pointer; border: 2px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,.2); }
`;
const DrawerFooter = styled.div`
  border-top: 1px solid ${COLORS.line}; padding: 12px; display: grid; gap: 8px;
`;
const ClearFiltersButton = styled.button`
  width: 100%; height: 44px; border-radius: 12px; border: 1px solid ${COLORS.line};
  background: ${COLORS.text}; color:#fff; font-weight:800; cursor:pointer;
  transition: filter .15s ease, transform .15s ease;
  &:hover{ filter: brightness(.95); transform: translateY(-1px); }
`;

/* ================= GRID / CARDS ================= */
const PerfumeGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(5, minmax(0, 1fr));

  @media (max-width: 1200px) { grid-template-columns: repeat(4, minmax(0, 1fr)); gap:14px; }
  @media (max-width: 900px)  { grid-template-columns: repeat(3, minmax(0, 1fr)); gap:12px; }
  @media (max-width: 640px)  { grid-template-columns: repeat(2, minmax(0, 1fr)); gap:10px; }
  @media (max-width: 380px)  { grid-template-columns: repeat(2, minmax(0, 1fr)); gap:8px; }
`;

const PerfumeCard = styled.div`
  background: ${COLORS.card};
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  padding: 12px;
  position: relative;
  transition: box-shadow .2s ease, transform .2s ease, border-color .2s ease;
  cursor: pointer;
  min-width: 0;      /* მნიშვნელოვანია, რომ სვეტში არ „გაბეროს“ */
  box-sizing: border-box;

  &:hover { box-shadow: ${COLORS.shadow}; transform: translateY(-2px); border-color: rgba(0,0,0,.12); }

  .hover-options {
    display: ${(p) => (p.show ? 'flex' : 'none')};
    position: absolute; inset: 0;
    background: rgba(255,255,255,.6); backdrop-filter: blur(6px);
    align-items: center; justify-content: center; z-index: 2; border-radius: 16px;
  }

  .view-btn{
    border: 1px solid ${COLORS.line}; background:#fff; border-radius: 12px; height: 38px; padding: 0 12px; font-weight: 800; cursor: pointer;
    transition: transform .15s ease, box-shadow .15s ease; color: black;
  }
  .view-btn:hover{ transform: translateY(-1px); box-shadow: ${COLORS.shadow}; }
  
    @media (max-width: 640px) {
    padding: 8px;
    border-radius: 12px;
  }
  @media (max-width: 380px) {
    padding: 6px;
    border-radius: 10px;
  }

`;

const blink = keyframes` 0%{opacity:1} 50%{opacity:.2} 100%{opacity:1} `;
const SaleBadge = styled.div`
  position: absolute; top: 10px; right: 10px;
  background: #ef4444; color: #fff; font-weight: 800;
  padding: 6px 10px; border-radius: 999px; font-size: 11px;
  box-shadow: 0 6px 16px rgba(239,68,68,.35);
  animation: ${blink} 1.5s infinite;
  z-index: 100;

  @media (max-width: 640px) { top: 8px; right: 8px; font-size: 10px; padding: 5px 8px; }
`;

const ImgWrap = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  background: ${COLORS.subtle};
  display: grid;
  place-items: center;
  overflow: hidden;
  margin-bottom: 10px;
  position: relative;

  @media (max-width: 640px) { margin-bottom: 6px; }
`;

const PerfumeImage = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;     /* პროპორციულად ჩასვავს ჩარჩოში */
  object-position: center; /* სურვილისამებრ: ცენტრში გაასწორებს */
  display: block;          /* inline-gap აღარ იქნება */
  @media (max-width: 640px) { width: 80%; height: 80%; }

  @media (max-width: 640px) { width: 80%; height: 80%; }
  @media (max-width: 380px) { width: 80%; height: 80%; }
`;

const CardRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 6px;
`;

const PerfumeName = styled.h3`
  font-size: 14px; font-weight: 800; color: ${COLORS.text};
  margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;

  @media (max-width: 640px) { font-size: 13px; }
  @media (max-width: 380px) { font-size: 12.5px; }
`;

const PerfumeBrand = styled.div`
  font-size: 12px; color: ${COLORS.dim};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;

  @media (max-width: 640px) { font-size: 11px; }
`;

const PriceBlock = styled.div`
  margin-top: 8px; font-weight: 800; color: ${COLORS.text}; font-size: 13px;
  .old { color:#9ca3af; text-decoration: line-through; margin-right: 6px; }

  @media (max-width: 640px) { font-size: 12px; }
`;

const IconBar = styled.div`
  position: absolute; left: 10px; top: 10px; display: flex; gap: 8px; z-index: 3;
  .icon-btn{
    width: 36px; height: 36px; border-radius: 10px; border: 1px solid ${COLORS.line};
    background:#fff; display:grid; place-items:center; cursor:pointer;
    transition: transform .15s ease, box-shadow .15s ease;
  }
  .icon-btn:hover{ transform: translateY(-1px); box-shadow: ${COLORS.shadow}; }
`;

/* ================= LOAD MORE ================= */
const LoadMoreButton = styled.button`
  display: block; margin: 24px auto 40px; height: 44px; padding: 0 16px;
  background: ${COLORS.text}; color:#fff; border: 0; border-radius: 12px; font-weight: 800; cursor:pointer;
  transition: transform .15s ease, filter .15s ease;
  &:hover{ transform: translateY(-1px); filter: brightness(.95); }
`;

/* ================= COMPONENT ================= */
const CategoryPage = ({ addToCart }) => {
  const { version } = useDataRefresh();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialCategory = params.get("category") || "";
  const initialGender = params.get("gender") || "";
  const initialSearch = (params.get("search") || "").trim();
  const { category, sex } = useParams();

  const [perfumes, setPerfumes] = useState([]);
  const [filteredPerfumes, setFilteredPerfumes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [aromas, setAromas] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedAromas, setSelectedAromas] = useState([]);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [brandExpanded, setBrandExpanded] = useState(false);
  const [aromaExpanded, setAromaExpanded] = useState(false);
  const [genderExpanded, setGenderExpanded] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');
  const [aromaSearch, setAromaSearch] = useState('');
  const [sortDropdownVisible, setSortDropdownVisible] = useState(false);
  const [sortOrder, setSortOrder] = useState('');
  const [activeCard, setActiveCard] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [isPriceFilterActive, setIsPriceFilterActive] = useState(false);
  const [selectedGenders, setSelectedGenders] = useState(initialGender ? [initialGender] : []);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [visiblePerfumes, setVisiblePerfumes] = useState(40);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
    const [categoryExpanded, setCategoryExpanded] = useState(false);
 const [subcategories, setSubcategories] = useState([]);
 const [selectedSubcategories, setSelectedSubcategories] = useState([]);
 const [subcategoryExpanded, setSubcategoryExpanded] = useState(false);
const [silentRefreshing, setSilentRefreshing] = useState(false);
const firstLoadRef = useRef(true);

  const dropdownRef = useRef(null);
  const filterMenuRef = useRef(null);

const navigate = useNavigate();

// URL query-ს უსაფრთხოდ შესაცვლელად (React Router-საც აცნობებს ცვლილებას)
const setQueryParam = (key, value) => {
  const u = new URL(window.location.href);

  if (Array.isArray(value)) {
    if (value.length) u.searchParams.set(key, value.join(","));
    else u.searchParams.delete(key);
  } else {
    if (value) u.searchParams.set(key, value);
    else u.searchParams.delete(key);
  }

  // აცნობე Router-ს, რომ URL შეიცვალა (replace, რომ history-ში ახალი ჩანაწერი არ დაემატოს)
  navigate(`${u.pathname}?${u.searchParams.toString()}`, { replace: true });
};

const clearQueryParams = (keys) => {
  const u = new URL(window.location.href);
  keys.forEach((k) => u.searchParams.delete(k));
  navigate(`${u.pathname}?${u.searchParams.toString()}`, { replace: true });
};



 const ucFirst = (s = "") => {
   const trimmed = String(s).trim();
   if (!trimmed) return "";
   return trimmed.charAt(0).toLocaleUpperCase() + trimmed.slice(1);
 };


// URL / State-იდან ამოიკითხე ერთჯერადად საწყისი არომატები
const urlAromas =
  params.get("aromas")?.split(",").map(s => s.trim()).filter(Boolean) || [];
const stateAromas = (location.state && Array.isArray(location.state.selectedAromas))
  ? location.state.selectedAromas
  : [];
const initialAromas = stateAromas.length ? stateAromas : urlAromas;

// --- Card helpers (image / price / stock) ---
// helpers (ჩასვი იმავე ადგილას, სადაც სხვა ჰელპერებია)
const isPerMlCategory = (cat) =>
  ["atomizers", "skin-care", "hair-and-body-care"].includes(cat);

const getDefaultMlSelection = (p = {}) => {
  if (!Array.isArray(p.milliliters) || p.milliliters.length === 0) return null;
  const cat = String(p.category || "").toLowerCase();

  // მარტო რიცხვითი ზომები
  let mls = p.milliliters
    .map(ml => ({
      ...ml,
      sizeNum: Number(ml.size),
      priceNum: Number(ml.price),
      saleNum: ml.sale_price != null ? Number(ml.sale_price) : null,
      unitStockNum: Number(ml.unit_stock ?? 0),
    }))
    .filter(ml => Number.isFinite(ml.sizeNum) && ml.sizeNum > 0 && Number.isFinite(ml.priceNum) && ml.priceNum > 0);

  if (isPerMlCategory(cat)) {
    // per-ML კატეგორიები → მხოლოდ ის ML-ები, სადაც unit_stock > 0
    mls = mls.filter(ml => ml.unitStockNum > 0);
    if (!mls.length) return null;
  } else if (cat === "perfume") {
    // pooled stock: products.full_stock უნდა ეყოს size-ს მაინც
    const fullStock = Number(p.full_stock ?? 0);
    mls = mls.filter(ml => fullStock >= ml.sizeNum);
    if (!mls.length) return null;
  }

  // ყველაზე პატარა ზომა
  mls.sort((a, b) => a.sizeNum - b.sizeNum);
  return mls[0] || null;
};

// ერთი ვარიაციის სურათის ამომღები (იჭერს როგორც images:[], ასევე image_url ველმა)
const getVariantImage = (v = {}) => {
  // შესაძლებელია სხვადასხვა ფორმატი:
  // v.images -> ["url1", "url2"] ან [{image_url:"..."}, ...]
  if (Array.isArray(v.images) && v.images.length) {
    const first = v.images[0];
    if (typeof first === "string" && first.trim()) return first;
    if (first && typeof first.image_url === "string" && first.image_url.trim()) return first.image_url;
  }
  // ზოგჯერ პირდაპირ v.image_url ან v.photo
  if (typeof v.image_url === "string" && v.image_url.trim()) return v.image_url;
  if (typeof v.photo === "string" && v.photo.trim()) return v.photo;
  return null;
};

// აქსესუარისთვის პირველი ხელმისაწვდომი ვარიაციის სურათი
const getFirstAccessoryVariantImage = (p = {}) => {
  const variants = getAccessoryVariants(p);
  if (!Array.isArray(variants) || !variants.length) return null;

  // წავიკითხოთ ჯერ stock>0 & active ვარიაციებიდან, მერე უბრალო აქტიურიდან, ბოლოს ნებისმიერი
  const by = (arr) => {
    for (const v of arr) {
      const url = getVariantImage(v);
      if (url) return url;
    }
    return null;
  };

  const active = variants.filter(v => Number(v.is_active ?? 1) === 1);
  const inStock = active.filter(v => Number(v.stock ?? v.unit_stock ?? 0) > 0);

  return by(inStock) || by(active) || by(variants) || null;
};


const getCardImage = (p = {}) => {
  const fallback1x1 = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

  if (p.category === "accessories") {
    const acc = getFirstAddedAccessoryImage(p);
    return acc || fallback1x1;
  }

  // დანარჩენი კატეგორიებისთვის შენი ძველი ჯაჭვი
  if (Array.isArray(p.imageUrls) && p.imageUrls[0]) return p.imageUrls[0];
  if (Array.isArray(p.images) && p.images[0]) return typeof p.images[0] === "string" ? p.images[0] : (p.images[0].image_url || p.images[0].url || p.images[0].src);
  if (p.image) return p.image;

  if (Array.isArray(p.milliliters)) {
    const withImgs = p.milliliters.find(ml => Array.isArray(ml.images) && ml.images.length);
    if (withImgs) return typeof withImgs.images[0] === "string" ? withImgs.images[0] : (withImgs.images[0].image_url || withImgs.images[0].url || withImgs.images[0].src);
  }

  return fallback1x1;
};


const getAccessoryVariants = (p = {}) =>
  p.accessory_variants || p.variants || p.product_accessory_variants || [];

const getAccessoryPrices = (p = {}) => {
  const variants = getAccessoryVariants(p);
  if (Array.isArray(variants) && variants.length) {
    const active  = variants.filter(v => Number(v.is_active ?? 1) === 1);
    const inStock = active.filter(v => Number(v.stock ?? v.unit_stock ?? 0) > 0);
    const pick    = inStock.length ? inStock : active.length ? active : variants;

    // მოვაგროვოთ regular და effective ფასები
    let minRegular = Infinity;
    let minEffective = Infinity;
    let anySale = false;

    for (const v of pick) {
      const regular = Number(v.price ?? v.price_p ?? 0);
      const sale    = Number(v.sale_p ?? 0);
      if (!regular || regular <= 0) continue;

      minRegular = Math.min(minRegular, regular);

      const eff = (sale > 0 && sale < regular) ? (anySale = true, sale) : regular;
      minEffective = Math.min(minEffective, eff);
    }

    if (minRegular === Infinity && minEffective === Infinity) {
      // ვერცერთი ნორმალური ფასი ვერ ვიპოვეთ → ვეცადოთ product-ის own price_p/sale_p
      const pPrice = Number(p.price_p || 0);
      const pSale  = Number(p.sale_p || 0);
      if (pPrice > 0) {
        if (pSale > 0 && pSale < pPrice) {
          return { price: pPrice, sale: pSale, hasSale: true };
        }
        return { price: pPrice, sale: 0, hasSale: false };
      }
      return { price: 0, sale: 0, hasSale: false };
    }

    // თუ იყო хоть ერთი sale_p<price → ვაჩვენოთ ძველი=minRegular და ახალი=minEffective
    if (anySale && minEffective < minRegular) {
      return { price: minRegular, sale: minEffective, hasSale: true };
    }
    return { price: (isFinite(minRegular) ? minRegular : minEffective), sale: 0, hasSale: false };
  }

  // fallback როცა variants საერთოდ არაა
  const price = Number(p.price_p || 0);
  const sale  = Number(p.sale_p  || 0);
  return sale > 0 && sale < price
    ? { price, sale, hasSale: true }
    : { price, sale: 0, hasSale: false };
};

// პირველ დამატებულ ვარიაციას ვპოულობ:
// პრიორიტეტი: stock>0 && active -> მხოლოდ active -> პირველი მნიშვნელობა
// "პირველად დამატებული" -> ვცდილობ შევაფასო created_at ASC; თუ არაა, size_sort ASC; ბოლოს array-ის პირველს.
const pickFirstAccessoryVariant = (p = {}) => {
  const variants = getAccessoryVariants(p);
  if (!Array.isArray(variants) || !variants.length) return null;

  const parseDate = (d) => (d ? Date.parse(d) || Infinity : Infinity);
  const normNum = (n) => (n == null ? Infinity : Number(n));

  const sorted = [...variants].sort((a, b) => {
    // created_at (ASC)
    const ca = parseDate(a.created_at);
    const cb = parseDate(b.created_at);
    if (ca !== cb) return ca - cb;

    // size_sort (ASC) — თუ არსებობს
    const sa = normNum(a.size_sort);
    const sb = normNum(b.size_sort);
    if (sa !== sb) return sa - sb;

    // id როგორც fallback (ASC)
    const ia = normNum(a.id || a.variant_id);
    const ib = normNum(b.id || b.variant_id);
    return ia - ib;
  });

  const active = sorted.filter(v => Number(v.is_active ?? 1) === 1);
  const inStock = active.filter(v => Number(v.stock ?? v.unit_stock ?? 0) > 0);

  return inStock[0] || active[0] || sorted[0] || null;
};

// აქსესუარის სურათად ავიღოთ "ყველაზე პირველი" —
// ჯერ product.images[0], თუ არაა -> ვარიაციები წაivre order-ში -> მერე imageUrls etc.
const getFirstAddedAccessoryImage = (p = {}) => {
  const takeFirstFromArray = (arr) => {
    if (!Array.isArray(arr) || !arr.length) return null;
    const first = arr[0];
    if (typeof first === "string") return first;
    if (first?.image_url) return first.image_url;
    if (first?.url) return first.url;
    if (first?.src) return first.src;
    return null;
  };

  // 1) პროდუქტის images-დან პირველი
  const fromProductImages = takeFirstFromArray(p.images);
  if (fromProductImages) return fromProductImages;

  // 2) ვარიაციების მიხედვით — პირვლად "დამატებულისგან" სცადე,
  //    თუ იმ ვარიაციას სურათი არ აქვს, მაშინ ყველა ვარიაციას პირველივე შეხვედრილი
  const firstVariant = pickFirstAccessoryVariant(p);
  const fromFirstVariant = firstVariant ? getVariantImage(firstVariant) : null;
  if (fromFirstVariant) return fromFirstVariant;

  const variants = getAccessoryVariants(p);
  if (Array.isArray(variants)) {
    for (const v of variants) {
      const u = getVariantImage(v);
      if (u) return u;
    }
  }

  // 3) imageUrls[0] ან სხვა single ველები
  const fromImageUrls = takeFirstFromArray(p.imageUrls);
  if (fromImageUrls) return fromImageUrls;

  const single = [p.image, p.cover, p.cover_url, p.thumbnail, p.photo, p.picture, p.main_image].find(
    (x) => typeof x === "string" && x.trim()
  );
  if (single) return single;

  return null;
};


const handleAddToCart = (e, p) => {
  e.stopPropagation();

  // ----- ACCESSORIES -----
  if (p.category === "accessories") {
    const v = pickFirstAccessoryVariant(p);
    if (!v) return;

    // normalize სვლები: addToCart ელის selectedVariant-ს (camelCase)
    const payload = {
      ...p,
      accessory_variant_id: v.id ?? v.variant_id ?? v.sku,
      selectedVariant: {
        id: v.id ?? v.variant_id ?? v.sku,
        color: v.color ?? v.color_ka ?? v.color_en ?? "",
        color_ka: v.color_ka ?? v.color ?? "",
        color_en: v.color_en ?? v.color ?? "",
        size_label: v.size_label ?? v.size ?? "",
        images: Array.isArray(v.images) ? v.images : [],
        price: v.price ?? v.price_p ?? 0,
        stock: v.stock ?? v.unit_stock ?? 0,
        is_active: v.is_active ?? 1,
      },
      // კომპატიბილობისთვის ზოგი ლოგიკა variant-საც კითხულობს
      variant: v,
      // დეფოლტად 1 ცალი
      quantity: 1,
    };

    addToCart(payload);
    return;
  }

  // ----- PERFUME / ATOMIZERS / CARE (ML-based) -----
  const cat = String(p.category || "").toLowerCase();
  if (["perfume", "atomizers", "skin-care", "hair-and-body-care"].includes(cat)) {
    const pickedMl = getDefaultMlSelection(p);
    // თუ ვერაფერი ავარჩიეთ, fallback — გადავუშვათ detail-ივით (შენი ძველი ქცევა)
    if (!pickedMl) {
      addToCart(p);
      return;
    }

    // ვაგზავნით detail-იზი ფორმატით → App.js addToCart უკვე მკვდება selectedMilliliters-ზე
    const quantities = { [String(pickedMl.size)]: 1 };
    const selectedMilliliters = [{
      size: pickedMl.size,
      price: pickedMl.price,
      sale_price: pickedMl.sale_price ?? null,
      images: Array.isArray(pickedMl.images) ? pickedMl.images : [],
      unit_stock: pickedMl.unit_stock ?? null,
      qty: 1, // პირდაპირაც ჩავწეროთ
    }];

    const payload = {
      ...p,
      // detail-ის რეჟიმის გასააქტიურებლად:
      selectedMilliliters,
      quantities, // App.js წაიკითხავს qty-ს ან ამ mapping-ს
    };

    addToCart(payload);
    return;
  }

  // ----- სხვა კატეგორიები (უბრალო ჩამატება) -----
  addToCart(p);
};

// ML-based stock:
// - perfume: pooled stock → უნდა ჰყოფნიდეს მინიმალურ ზომას (მაგ. 5/10ml)
// - atomizers/skin-care/hair-and-body-care: ერთ-ერთ ml-ში მაინც unit_stock > 0
const isMlBasedCategory = (cat) => ["perfume","atomizers","skin-care","hair-and-body-care"].includes(cat);

const getMinMlSize = (p) => {
  if (!Array.isArray(p.milliliters) || !p.milliliters.length) return 0;
  const sizes = p.milliliters
    .map(ml => Number(ml.size))
    .filter(n => !Number.isNaN(n) && n > 0);
  return sizes.length ? Math.min(...sizes) : 0;
};

const hasAddToCartStock = (p = {}) => {
  const cat = p.category;
  const fullStock = Number(p.full_stock ?? 0);

  // საერთო ჰელპერი: არსებობს თუ არა ერთეულად გაყიდვადი ვარიანტი მარაგში
  const hasUnitVariantInStock =
    Array.isArray(p.milliliters) &&
    p.milliliters.some(ml =>
      Number(ml.unit_stock ?? 0) > 0 &&
      (ml.is_active == null || Number(ml.is_active) === 1) &&
      (ml.is_unit == null || Number(ml.is_unit) === 1)
    );

  if (cat === "accessories") {
    const variants = getAccessoryVariants(p);
    if (Array.isArray(variants) && variants.length) {
      return variants.some(v =>
        Number(v.stock ?? 0) > 0 && Number(v.is_active ?? 1) === 1
      );
    }
    // fallback როცა ვარიაციები არაა
    return fullStock >= 1;
  }

  if (cat === "perfume") {
    const minSize = getMinMlSize(p);
    // ან საკმარისი ჩამოსასხმელი გაქვს, ან ერთეულოვანი ვარიანტი აქვს სტოკში
    return (minSize > 0 && fullStock >= minSize) || hasUnitVariantInStock;
  }

  if (isPerMlCategory(cat)) {
    // per-ml კატეგორიებისთვის საკმარისია ერთეულოვანი ვარიანტის სტოკი
    return hasUnitVariantInStock;
  }

  return fullStock >= 1;
};


// ერთი useEffect – მხოლოდ საწყისად ჩასასმელად
useEffect(() => {
  if (initialAromas.length) {
    setSelectedAromas(initialAromas);
  }
  // არანაირი navigate აქ! CategoryPage უნდა უბრალოდ წაიკითხოს და აჩვენოს.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []); 


  useEffect(() => { document.title = t("titles.category"); }, [language]);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const s = p.get("search") || "";
    setSearchQuery(s);
    setVisiblePerfumes(40);
    setSearchTerm(s);
     // NEW: subcategory მხარდაჭერა URL-იდან (comma-separated-იც)
 const subs = (p.get("subcategory") || "")
   .split(",")
   .map(v => v.trim())
   .filter(Boolean);
 setSelectedSubcategories(subs);
  }, [location.search]);


  useEffect(() => {
    setSelectedGenders(initialGender ? [initialGender] : []);
    setSelectedCategories(initialCategory ? [initialCategory] : []);
  }, [initialGender, initialCategory]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) setFiltersVisible(false);
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setSortDropdownVisible(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isAllCategories = location.pathname === '/products';

 useEffect(() => {
   let alive = true;
   (async () => {
     setLoading(true);            // სპინერი მხოლოდ პირველ ჩატვირთვაზე / რეალურ როუტ-ცვლილებაზე
     try {
       // ===== იგივე fetching ლოგიკა, უბრალოდ აქ ვამატებთ ?v=version cache-bust-ად =====
       let perfumesData = [];
       if (location.pathname === "/best-sellers") {
         const bestSellersResponse = await api.get(`${API_BASE_URL}/get_all_favorite_perfumes.php`, { params: { v: version }});
         perfumesData = bestSellersResponse.data.perfumes || [];
       } else {
         const response = await api.get(`${API_BASE_URL}/get_products.php`, { params: { v: version }});
         perfumesData = response.data.map((perfume) => ({
           ...perfume,
imageUrls: Array.isArray(perfume.imageUrls) ? perfume.imageUrls
         : (perfume.image ? [perfume.image] : []),
           price: perfume.milliliters?.[0]?.price ? parseFloat(perfume.milliliters[0].price) : 0,
         }));
         const bestSellersResponse = await api.get(`${API_BASE_URL}/get_all_favorite_perfumes.php`, { params: { v: version }});
         let bestSellers = bestSellersResponse.data.perfumes || [];
         perfumesData = perfumesData.map((perfume) => ({
           ...perfume,
           isBestSeller: bestSellers.some((bs) => bs.id === perfume.product_id),
         })).sort((a,b) => (b.isBestSeller?1:0) - (a.isBestSeller?1:0));
       }

       let filtered = perfumesData;
       if (!isAllCategories && location.pathname !== "/best-sellers") {
         filtered = perfumesData.filter((p) => p.category === category && p.sex === sex);
       }

const allPrices = perfumesData.flatMap((p) => {
  if (p.category === "accessories") {
    const vs = getAccessoryVariants(p);
    const vPrices = Array.isArray(vs) ? vs.map(v => Number(v.price || v.price_p || 0)).filter(n => n > 0) : [];
    // თუ საერთოდ არ არის ვარიაციაში ფასი, ვეცადოთ product.price_p
    return vPrices.length ? vPrices : [Number(p.price_p || 0)].filter(n => n > 0);
  }
  // დანარჩენი კატეგორიები — ML ფასები
  return Array.isArray(p.milliliters)
    ? p.milliliters.map(ml => Number(ml.price || 0)).filter(n => n > 0)
    : [];
});
       if (!alive) return;
       if (allPrices.length > 0) {
         const min = Math.min(...allPrices);
         const max = Math.max(...allPrices);
         setMinPrice(min); setMaxPrice(max); setPriceRange([min, max]);
       }
       setPerfumes(filtered);
       setFilteredPerfumes(filtered);
       const uniqueBrands = [...new Set(
        filtered
          .map(p => (p.brand ?? "").trim())
          .filter(Boolean) // მოშორებს "", null, undefined
      )].sort((a, b) => a.localeCompare(b));
      setBrands(uniqueBrands);
       const computedAromas = [
         ...new Set(filtered.flatMap((p) => p.aroma_en ? p.aroma_en.split(",").map((a) => a.trim()) : [])),
       ];
       // მონიშნულები არ დაიკარგოს
       setAromas((prev) => {
         const merged = new Set([...(computedAromas || []), ...(selectedAromas || [])]);
         return Array.from(merged).sort((a,b)=>a.localeCompare(b));
       });
     } catch (e) {
       console.error("❌ Error fetching perfumes:", e);
     } finally {
       if (!alive) return;
       firstLoadRef.current = false;
       setTimeout(() => setLoading(false), 200);
     }
   })();
   const handleDocClick = () => setActiveCard(null);
   document.addEventListener("click", handleDocClick);
   return () => { alive = false; document.removeEventListener("click", handleDocClick); };
 // აქედან ამოვიღეთ version, რომ სპინერი არ ჩაინთოს ყოველ live update-ზე
 }, [category, sex, isAllCategories, location.pathname]);

 useEffect(() => {
  if (firstLoadRef.current) return;  // პირველი ჩატვირთვა უკვე ზემოთ მოვაგვარეთ
  let alive = true;
  (async () => {
    try {
      setSilentRefreshing(true); // სურვილისამებრ: შეიძლება პატარა indicator სადმე
      // იგივე request-ები, რაც ზემოთ, უბრალოდ setLoading არ არის
      let perfumesData = [];
      if (location.pathname === "/best-sellers") {
        const bestSellersResponse = await api.get(`${API_BASE_URL}/get_all_favorite_perfumes.php`, { params: { v: version }});
        perfumesData = bestSellersResponse.data.perfumes || [];
      } else {
        const response = await api.get(`${API_BASE_URL}/get_products.php`, { params: { v: version }});
        perfumesData = response.data.map((perfume) => ({
          ...perfume,
imageUrls: Array.isArray(perfume.imageUrls) ? perfume.imageUrls
         : (perfume.image ? [perfume.image] : []),
          price: perfume.milliliters?.[0]?.price ? parseFloat(perfume.milliliters[0].price) : 0,
        }));
        const bestSellersResponse = await api.get(`${API_BASE_URL}/get_all_favorite_perfumes.php`, { params: { v: version }});
        let bestSellers = bestSellersResponse.data.perfumes || [];
        perfumesData = perfumesData.map((perfume) => ({
          ...perfume,
          isBestSeller: bestSellers.some((bs) => bs.id === perfume.product_id),
        })).sort((a,b) => (b.isBestSeller?1:0) - (a.isBestSeller?1:0));
      }
      let filtered = perfumesData;
      if (!isAllCategories && location.pathname !== "/best-sellers") {
        filtered = perfumesData.filter((p) => p.category === category && p.sex === sex);
      }
      if (!alive) return;
      setPerfumes(filtered);
      // არ ვეხებით scroll-ს, არ ვრთავთ ლოდერს, არ ვცვლით user-ის მიერ დაყენებულ ფილტრებს
      // applyFilters უკვე გამოიძახება ქვემოთ არსებული useEffect-ით
    } catch (e) {
      // ჩუმად ჩავაგდოთ — live refresh-ში UI არ უნდა „იკრუნჩხებოდეს“
      console.error(e);
    } finally {
      if (alive) setSilentRefreshing(false);
    }
  })();
  return () => { alive = false; };
}, [version]); // ← მხოლოდ live update-ზე


  useEffect(() => {
    if (perfumes.length > 0) {
      const uniqueCategories = [...new Set(perfumes.map((p) => p.category))];
      setCategories(uniqueCategories);
         // robust ამოღება: ვცდილობთ ორივე ველის მხარდაჭერას (subcategory | sub_category)
   const getSub = (p) => (p.subcategory ?? p.sub_category ?? "").trim();
   const uniqueSubs = [...new Set(perfumes.map(getSub).filter(Boolean))].sort((a,b)=>a.localeCompare(b));
   setSubcategories(uniqueSubs);

    }
  }, [perfumes]);

  const toggleFiltersVisibility = () => setFiltersVisible((prev) => !prev);
  const toggleSection = (setter, val) => setter(!val);

  const applyFilters = () => {
    let filtered = perfumes;

    if (searchTerm && searchTerm.length > 0) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q));
    }
    if (searchQuery && searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q));
    }

if (isPriceFilterActive) {
  filtered = filtered.filter((item) =>
    item.category === "accessories"
      ? (() => {
          const { price } = getAccessoryPrices(item);
          return price >= priceRange[0] && price <= priceRange[1];
        })()
      : item.milliliters?.some((ml) =>
          parseFloat(ml.price) >= priceRange[0] && parseFloat(ml.price) <= priceRange[1]
        )
  );
}

    if (selectedCategories.length > 0) filtered = filtered.filter((p) => selectedCategories.includes(p.category));
     if (selectedSubcategories.length > 0) {
   filtered = filtered.filter((p) => {
     const sub = (p.subcategory ?? p.sub_category ?? "").trim();
     return selectedSubcategories.includes(sub);
   });
 }

    if (selectedGenders.length) filtered = filtered.filter((p) => selectedGenders.includes(p.sex));
    if (selectedBrands.length) {
      filtered = filtered.filter((p) => selectedBrands.includes((p.brand ?? "").trim()));
    }
    if (selectedAromas.length) {
      filtered = filtered.filter((p) => selectedAromas.some((a) => p.aroma_en && p.aroma_en.includes(a)));
    }

    filtered = filtered.map((perfume) => ({
      ...perfume,
      sortedPrices: perfume.milliliters
        ? perfume.milliliters.map((ml) => parseFloat(ml.price)).filter((n) => n > 0).sort((a, b) => a - b)
        : [],
    }));

if (sortOrder) {
  filtered = [...filtered].sort((a, b) => {
    const priceA = a.category === "accessories"
      ? getAccessoryPrices(a).price
      : (a.sortedPrices[0] || 0);

    const priceB = b.category === "accessories"
      ? getAccessoryPrices(b).price
      : (b.sortedPrices[0] || 0);

    return sortOrder === "asc" ? priceA - priceB : priceB - priceA;
  });
}

    setFilteredPerfumes(filtered);

    const chips = [
      searchTerm && { type: "search", value: searchTerm },
      ...selectedBrands.map((v) => ({ type: "brand", value: v })),
      ...selectedCategories.map((v) => ({ type: "category", value: v })),
      ...selectedSubcategories.map((v) => ({ type: "subcategory", value: v })),
      ...selectedAromas.map((v) => ({ type: "aroma", value: v })),
      sortOrder && { type: "sort", value: sortOrder === "asc" ? "Min to Max" : "Max to Min" },
      isPriceFilterActive && { type: "price" },
      ...selectedGenders.map((v) => ({ type: "gender", value: v })),
    ].filter(Boolean);
    setAppliedFilters(chips);
  };

 useEffect(() => {
   applyFilters();
 }, [
   perfumes,                   // ← დაამატე ეს
   selectedCategories,
   selectedSubcategories,
   selectedBrands,
   selectedAromas,
   priceRange,
   sortOrder,
   isPriceFilterActive,
   selectedGenders,
   searchTerm,
   searchQuery
 ]);

  const removeFilter = (filter) => {
    if (filter.type === "brand") setSelectedBrands((prev) => prev.filter((b) => b !== filter.value));
     else if (filter.type === "subcategory") {
  setSelectedSubcategories((prev) => {
    const next = prev.filter((s) => s !== filter.value);
    setQueryParam("subcategory", next);
    return next;
  });
 }
else if (filter.type === "aroma") {
  setSelectedAromas((prev) => {
    const next = prev.filter((a) => a !== filter.value);
    // სურვილისამებრ: URL query-ის ატრიალება
    const u = new URL(window.location.href);
    if (next.length) u.searchParams.set("aromas", next.join(","));
    else u.searchParams.delete("aromas");
    window.history.replaceState({}, "", u);
    return next;
  });
}
    else if (filter.type === "price") { setIsPriceFilterActive(false); setPriceRange([minPrice, maxPrice]); }
    else if (filter.type === "sort") setSortOrder("");
    else if (filter.type === "gender") setSelectedGenders((prev) => prev.filter((g) => g !== filter.value));
    else if (filter.type === "category") 
  setSelectedCategories((prev) => {
    const next = prev.filter((c) => c !== filter.value);
    setQueryParam("category", next);
    return next;
  });
    else if (filter.type === "search") {
      setSearchTerm(""); setSearchQuery("");
      const url = new URL(window.location.href);
      url.searchParams.delete("search");
      window.history.replaceState({}, "", url);
    }
    setAppliedFilters((prev) => prev.filter((f) => f.type !== filter.type));
  };

  const clearFilters = () => {
    setSelectedBrands([]); setSelectedAromas([]); setSortOrder(''); setAppliedFilters([]);
    setIsPriceFilterActive(false); setPriceRange([minPrice, maxPrice]); setFilteredPerfumes(perfumes);
    setSelectedGenders([]); setSelectedCategories([]);
    setSearchTerm("");
    setSelectedSubcategories([]);
    setSearchQuery("");
     clearQueryParams([
   "search",
   "subcategory",
   "category",
   "gender",
   "brand",
   "aromas",
   "sort"
 ]);

  };

  const handleCardClick = (id, e) => { e.stopPropagation(); setActiveCard((p) => (p === id ? null : id)); };
  const handlePriceChange = (e) => {
    const value = Number(e.target.value);
    setIsPriceFilterActive(true);
    if (e.target.name === "min") setPriceRange([value, priceRange[1]]);
    else setPriceRange([priceRange[0], value]);
  };

  useEffect(() => {
  if (filtersVisible) {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }
}, [filtersVisible]);

useEffect(() => {
  // ბრაუზერს ვეუბნებით: არ შეინახო წინა scroll პოზიცია
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }
}, []);

 useEffect(() => {
   window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
 }, [location.pathname]); // ← მხოლოდ ბილდის (route) შეცვლაზე



  if (loading) return <PageLoader><Spinner/></PageLoader>;

  return (
    <>
      {/* ===== Sticky Top Bar ===== */}
      <TopBar>
        <TopBarInner>
          <LeftActions>
            <PillButton onClick={toggleFiltersVisibility}><FaFilter/>{t("categorypage.filter")}</PillButton>
          </LeftActions>

          <RightActions ref={dropdownRef}>
            <SortDropdownWrap>
              <PillButton onClick={() => setSortDropdownVisible((p) => !p)}>
                <FaSort/>{t("categorypage.sort")}
              </PillButton>
              <DropdownCard $hidden={!sortDropdownVisible}>
                <DropdownItem onClick={() => { setSortOrder('asc'); setSortDropdownVisible(false); }}>
                  {t("categorypage.mintomax")}
                </DropdownItem>
                <DropdownItem onClick={() => { setSortOrder('desc'); setSortDropdownVisible(false); }}>
                  {t("categorypage.maxtomin")}
                </DropdownItem>
              </DropdownCard>
            </SortDropdownWrap>
          </RightActions>
        </TopBarInner>

        {/* Applied filters chips */}
        <AppliedFiltersBox>
          {appliedFilters.length > 0 ? (
            appliedFilters.map((filter, i) => (
              <FilterTag key={i}>
                {filter.type === "brand"   ? `${t("categorypage.brand")}:`
                 : filter.type === "aroma"  ? `${t("categorypage.aroma")}:`
                 : filter.type === "sort"   ? `${t("categorypage.sort-filter")}:`
                 : filter.type === "gender" ? `${t("categorypage.gender")}:`
                 : filter.type === "category" ? `${t("categorypage.categories")}:`
                 : filter.type === "subcategory" ? `${t("categorypage.subcategories") || "Subcategories"}:`
                 : filter.type === "price"  ? `${t("categorypage.price")}: ${priceRange[0]} - ${priceRange[1]} ${t("newproducts.currency")}`
                 : filter.type}{" "}
                  {filter.type === "gender"
                      ? t(`categorypage.${filter.value}`)
                      : (filter.type === "subcategory"
                          ? ucFirst(filter.value)
                          : filter.value)}
                <span className="close" onClick={() => removeFilter(filter)}><FaTimes/></span>
              </FilterTag>
            ))
          ) : (
            <span style={{ color: COLORS.dim, fontSize: 13 }}>{t("categorypage.filteroff")}</span>
          )}
        </AppliedFiltersBox>
      </TopBar>

      {/* ===== Drawer Overlay & Menu ===== */}
      <Overlay $open={filtersVisible} onClick={() => setFiltersVisible(false)} />
      <FiltersMenu isVisible={filtersVisible} ref={filterMenuRef}>
        <FilterTop>
          <h3>{t("categorypage.filter")}</h3>
          <CloseButton onClick={() => setFiltersVisible(false)} aria-label="Close"><FaTimes/></CloseButton>
        </FilterTop>

        <FilterBody>
          {/* Categories */}
          <FilterSection>
            <FilterHeaderBtn onClick={() => toggleSection(setCategoryExpanded, categoryExpanded)}>
              <span>{t("categorypage.categories")}</span>
              {categoryExpanded ? <FaChevronUp/> : <FaChevronDown/>}
            </FilterHeaderBtn>
            <FilterOptions isExpanded={categoryExpanded}>
              <div className="scroll">
                {categories.map((cat) => (
                  <CheckboxItem key={cat}>
                    <input
                      type="checkbox"
                      value={cat}
                      checked={selectedCategories.includes(cat)}
                      onChange={(e) =>
                        setSelectedCategories((prev) => e.target.checked ? [...prev, cat] : prev.filter((c) => c !== cat))
                      }
                    />
                    <span></span>{t(`categorypage.${cat}`)}
                  </CheckboxItem>
                ))}
              </div>
            </FilterOptions>
          </FilterSection>


          {/* Subcategories */}
<FilterSection>
  <FilterHeaderBtn onClick={() => toggleSection(setSubcategoryExpanded, subcategoryExpanded)}>
    <span>{t("categorypage.subcategories") || "Subcategories"}</span>
    {subcategoryExpanded ? <FaChevronUp/> : <FaChevronDown/>}
  </FilterHeaderBtn>

  <FilterOptions isExpanded={subcategoryExpanded}>
    <div className="scroll">
      {subcategories.map((sub) => (
        <CheckboxItem key={sub}>
          <input
            type="checkbox"
            value={sub}
            checked={selectedSubcategories.includes(sub)}
            onChange={(e) =>
              setSelectedSubcategories((prev) =>
                e.target.checked ? [...prev, sub] : prev.filter((s) => s !== sub)
              )
            }
          />
 {(() => {
     const label = t(`categorypage.${sub}`) || sub;
     return (<><span></span>{ucFirst(label)}</>);
   })()}
        </CheckboxItem>
      ))}
    </div>
  </FilterOptions>
</FilterSection>


          {/* Gender */}
          <FilterSection>
            <FilterHeaderBtn onClick={() => toggleSection(setGenderExpanded, genderExpanded)}>
              <span>{t("categorypage.gender")}</span>
              {genderExpanded ? <FaChevronUp/> : <FaChevronDown/>}
            </FilterHeaderBtn>
            <FilterOptions isExpanded={genderExpanded}>
              <div className="scroll">
                {["man", "woman", "kids", "unisex"].map((g) => (
                  <CheckboxItem key={g}>
                    <input
                      type="checkbox"
                      value={g}
                      checked={selectedGenders.includes(g)}
                      onChange={(e) =>
                        setSelectedGenders((prev) => e.target.checked ? [...prev, g] : prev.filter((x) => x !== g))
                      }
                    />
                    <span></span>{t(`categorypage.${g}`)}
                  </CheckboxItem>
                ))}
              </div>
            </FilterOptions>
          </FilterSection>

          {/* Brands */}
{/* BRANDS */}
<FilterSection>
  <FilterHeaderBtn onClick={() => toggleSection(setBrandExpanded, brandExpanded)}>
    <span>{t("categorypage.brands")}</span>
    {brandExpanded ? <FaChevronUp/> : <FaChevronDown/>}
  </FilterHeaderBtn>

  <FilterOptions isExpanded={brandExpanded} $tall>
    <div className="content">
      <SearchContainer>
        <SearchInput
          type="text"
          placeholder={t("categorypage.searchbrands")}
          onChange={(e) => setBrandSearch(e.target.value)}
          value={brandSearch}
        />
        <FaSearch/>
      </SearchContainer>
    </div>

    <div className="scroll">
    {brands
      .filter(b => b && b.toLowerCase().includes(brandSearch.toLowerCase()))
      .map((brand, idx) => (
          <CheckboxItem key={idx}>
            <input
              type="checkbox"
              value={brand}
              checked={selectedBrands.includes(brand)}
              onChange={(e) =>
                setSelectedBrands((prev) =>
                  e.target.checked ? [...prev, brand] : prev.filter((x) => x !== brand)
                )
              }
            />
            <span></span>{brand}
          </CheckboxItem>
        ))}
    </div>
  </FilterOptions>
</FilterSection>

          {/* Aroma (only for perfume) */}
          {filteredPerfumes.some((p) => p.category === "perfume") && (
<FilterSection>
  <FilterHeaderBtn onClick={() => toggleSection(setAromaExpanded, aromaExpanded)}>
    <span>{t("categorypage.aroma")}</span>
    {aromaExpanded ? <FaChevronUp/> : <FaChevronDown/>}
  </FilterHeaderBtn>

  <FilterOptions isExpanded={aromaExpanded} $tall>
    <div className="content">
      <SearchContainer>
        <SearchInput
          type="text"
          placeholder={t("categorypage.searcharoma")}
          onChange={(e) => setAromaSearch(e.target.value)}
          value={aromaSearch}
        />
        <FaSearch/>
      </SearchContainer>
    </div>

    <div className="scroll">
      {aromas
        .filter((a) => a.toLowerCase().includes(aromaSearch.toLowerCase()))
        .map((aroma, idx) => (
          <CheckboxItem key={idx}>
            <input
              type="checkbox"
              value={aroma}
              checked={selectedAromas.includes(aroma)}
              onChange={(e) =>
                setSelectedAromas((prev) =>
                  e.target.checked ? [...prev, aroma] : prev.filter((x) => x !== aroma)
                )
              }
            />
            <span></span>{aroma}
          </CheckboxItem>
        ))}
    </div>
  </FilterOptions>
</FilterSection>
          )}

          {/* Price */}
          <FilterSection>
            <FilterHeaderBtn as="div">
              <span>{t("categorypage.price")}</span>
            </FilterHeaderBtn>
            <div style={{ padding: 12 }}>
              <PriceFilterContainer>
                <PriceRangeWrapper>
                  <PriceLabel>
                    {t("categorypage.minprice")} <b>{priceRange[0]} {t("newproducts.currency")}</b>
                  </PriceLabel>
                  <PriceRangeInput
                    type="range"
                    name="min"
                    min={minPrice}
                    max={maxPrice || 0}
                    value={priceRange[0]}
                    onChange={handlePriceChange}
                  />
                </PriceRangeWrapper>

                <PriceRangeWrapper>
                  <PriceLabel>
                    {t("categorypage.maxprice")} <b>{priceRange[1]} {t("newproducts.currency")}</b>
                  </PriceLabel>
                  <PriceRangeInput
                    type="range"
                    name="max"
                    min={minPrice}
                    max={maxPrice || 0}
                    value={priceRange[1]}
                    onChange={handlePriceChange}
                  />
                </PriceRangeWrapper>
              </PriceFilterContainer>
            </div>
          </FilterSection>
        </FilterBody>

        <DrawerFooter>
          <ClearFiltersButton onClick={clearFilters}>{t("categorypage.clearfilters")}</ClearFiltersButton>
        </DrawerFooter>
      </FiltersMenu>

      {/* ===== GRID ===== */}
      <GridWrap>
        <PerfumeGrid>
          {filteredPerfumes.slice(0, visiblePerfumes).map((perfume) => {
            const isMlBasedCard = ["perfume", "skin-care", "hair-and-body-care"].includes(perfume.category);
            const minMlSize =
              isMlBasedCard && Array.isArray(perfume.milliliters) && perfume.milliliters.length
                ? Math.min(
                    ...perfume.milliliters.map((ml) => parseFloat(ml.size)).filter((n) => !isNaN(n) && n > 0)
                  )
                : 0;

            const showAddToCart = hasAddToCartStock(perfume);

            return (
              <PerfumeCard
                key={perfume.product_id}
                onClick={(e) => handleCardClick(perfume.product_id, e)}
                show={activeCard === perfume.product_id}
              >
{(
  // ML-based sale?
  (perfume.milliliters && Array.isArray(perfume.milliliters) &&
    perfume.milliliters.some((ml) => Number(ml.sale_price) > 0 && Number(ml.sale_price) < Number(ml.price)))
  // product-level price_p/sale_p (თუ გაქვს non-accessory-ზე)
  || (perfume.sale_p && Number(perfume.sale_p) > 0 && Number(perfume.sale_p) < Number(perfume.price_p))
  // accessories: variant-level sale_p
  || (perfume.category === "accessories" && getAccessoryPrices(perfume).hasSale)
) ? (
  <SaleBadge>{t("newproducts.sale")}</SaleBadge>
) : null}

                <div className="hover-options">
                  <Link to={`/product/${perfume.product_id}`} onClick={(e) => e.stopPropagation()}>
                    <button className="view-btn">{t("categorypage.viewmore")}</button>
                  </Link>
                </div>

                <ImgWrap>
                  <PerfumeImage
                    src={getCardImage(perfume)}
                    alt={perfume.name}
                    onError={(e) => { e.currentTarget.src = getCardImage({}); }} // fallback, თუ ლინკი გაფუჭდა
                  />
                </ImgWrap>

                <PerfumeName title={perfume.name}>{perfume.name}</PerfumeName>
                <PerfumeBrand title={perfume.brand}>{perfume.brand}</PerfumeBrand>

                <PriceBlock>
 {perfume.category === "accessories" ? (
   (() => {
     // ვაჩვენებთ იმავე „პირველ/დეფოლტ“ ვარიანტის ფასს, რასაც სურათისთვის/დამატებისთვის ვიყენებთ
     const v = pickFirstAccessoryVariant(perfume);
     if (!v) return <span>N/A</span>;
     const regular = Number(v.price ?? v.price_p ?? 0);
     const sale    = Number(v.sale_p ?? 0);
     const onSale  = sale > 0 && sale < regular;

     if (!regular) return <span>N/A</span>;
     return onSale ? (
       <>
         <span className="old">{regular} {t("newproducts.currency")}</span>
         <span>{sale} {t("newproducts.currency")}</span>
       </>
     ) : (
       <span>{regular} {t("newproducts.currency")}</span>
     );
   })()
 ) : perfume.milliliters?.length > 0 ? (
  // ... შენი არსებული ML price range ლოგიკა უცვლელად დატოვე
  (() => {
    const originalPrices = perfume.milliliters.map((ml) => parseFloat(ml.price));
    const salePrices = perfume.milliliters.map((ml) => parseFloat(ml.sale_price) || parseFloat(ml.price));
    const minOriginal = Math.min(...originalPrices);
    const maxOriginal = Math.max(...originalPrices);
    const minSale = Math.min(...salePrices);
    const maxSale = Math.max(...salePrices);
    const changed = minSale < minOriginal || maxSale < maxOriginal;

    return (
      <>
        {changed ? (
          <>
            <span className="old">
              {minOriginal === maxOriginal
                ? `${minOriginal} ${t("newproducts.currency")}`
                : `${minOriginal} - ${maxOriginal} ${t("newproducts.currency")}`}
            </span>
            <span>
              {minSale === maxSale
                ? `${minSale} ${t("newproducts.currency")}`
                : `${minSale} - ${maxSale} ${t("newproducts.currency")}`}
            </span>
          </>
        ) : (
          <span>
            {minOriginal === maxOriginal
              ? `${minOriginal} ${t("newproducts.currency")}`
              : `${minOriginal} - ${maxOriginal} ${t("newproducts.currency")}`}
          </span>
        )}
      </>
    );
  })()
) : (
  "N/A"
)}
                </PriceBlock>

                <IconBar>
                  {showAddToCart && (
                  <button
                    className="icon-btn"
                    onClick={(e) => handleAddToCart(e, perfume)}
                    aria-label="Add to cart"
                    title={t("categorypage.addtocart") || "Add to cart"}
                  >
                    <ShoppingCart size={18} />
                  </button>
                  )}
                  <div className="icon-btn" onClick={(e) => e.stopPropagation()}>
                    <FavoriteButton productId={perfume.product_id} initialIsFavorite={perfume.is_favorite} />
                  </div>
                </IconBar>
              </PerfumeCard>
            );
          })}
        </PerfumeGrid>

        {visiblePerfumes < filteredPerfumes.length && (
          <LoadMoreButton onClick={() => setVisiblePerfumes((v) => v + 8)}>
            {t("purchases.loadmore")}
          </LoadMoreButton>
        )}
      </GridWrap>
    </>
  );
};

export default CategoryPage;
